import React, { useRef } from "react";
import EditableSection from "../EditableSection";
import CustomToast from "../CustomToast";
import config from "../../config";
import { useClientAuth } from "../../context/ClientAuthContext";
import { validators } from "../../utils/validators";

// Field configs - the single source of truth for what each section shows
// and which of those rows are editable. Adding a new editable field later
// (address, designation, etc.) is just adding an entry with `editable: true`
// here; the component logic in EditableSection/useEditableSection doesn't
// need to change.
const getBasicDetailsFields = (clientDetails) => [
    {
        key: "Billing_name",
        label: "Full Name",
        value: clientDetails.Billing_name,
        editable: true,
        validate: (value) => validators.text(value, { label: "Full Name" }),
    },
    {
        key: "email",
        label: "Email",
        value: clientDetails.email,
        editable: true,
        validate: (value) => validators.email(value, { label: "Email" }),
    },
    {
        key: "phone",
        label: "Phone No.",
        value: clientDetails.phone,
        editable: true,
        validate: (value) => validators.phone(value, { label: "Phone number" }),
    },
    {
        key: "createdAt",
        label: "Registration Date",
        value: new Date(clientDetails.createdAt).toLocaleDateString('en-GB'),
    },
    {
        key: "updatedAt",
        label: "Last Updated At",
        value: new Date(clientDetails.updatedAt).toLocaleDateString('en-GB'),
    },
];

const getCompanyDetailsFields = (detail) => [
    { key: "comp_name", label: "Company :", value: detail.comp_name },
    {
        key: "poc",
        label: "POC :",
        value: detail.poc,
        editable: true,
        validate: (value) => validators.text(value, { label: "POC" }),
    },
    {
        key: "phone",
        label: "Phone No. :",
        value: detail.phone,
        editable: true,
        validate: (value) => validators.phone(value, { label: "Phone number" }),
    },
    { key: "email", label: "Email :", value: detail.email },
    { key: "bill_name", label: "Bill Name :", value: detail.bill_name },
    { key: "gstin", label: "GST No. :", value: detail.gstin },
    { key: "pan", label: "Pan No. :", value: detail.pan },
    { key: "bill_address", label: "Address :", value: detail.bill_address },
];

function Client_accountdelails({ clientDetails, onUpdateClientDetails }) {
    const { apiRequest } = useClientAuth();
    const toastRef = useRef(null);

    const showMessage = (severity, detail) => {
        const summary = severity.charAt(0).toUpperCase() + severity.slice(1);
        toastRef.current.show(severity, summary, detail);
    };

    // Basic Details and Company Details live in separate tables (Client vs
    // ClientDetails), so each section saves through its own endpoint - but
    // each one is a single call carrying only the fields that changed.
    const saveBasicFields = async (changedFields) => {
        try {
            await apiRequest(
                `${config.apiBASEURL}/ClientRoutes/update-basic-details/${clientDetails.Client_id}`,
                { method: "PUT", data: { fields: changedFields } }
            );
            onUpdateClientDetails?.((prev) => ({ ...prev, ...changedFields }));
            showMessage("success", "Profile updated successfully.");
        } catch (err) {
            const message = err?.response?.data?.message || "Failed to update profile. Please try again.";
            showMessage("error", message);
            throw new Error(message);
        }
    };

    const saveCompanyFields = async (clientDetailsId, changedFields) => {
        try {
            await apiRequest(
                `${config.apiBASEURL}/ClientRoutes/update-client-detail/${clientDetailsId}`,
                { method: "PUT", data: { fields: changedFields } }
            );
            onUpdateClientDetails?.((prev) => ({
                ...prev,
                ClientDetails: prev.ClientDetails.map((d) =>
                    d.Client_details_id === clientDetailsId ? { ...d, ...changedFields } : d
                ),
            }));
            showMessage("success", "Company details updated successfully.");
        } catch (err) {
            const message = err?.response?.data?.message || "Failed to update company details. Please try again.";
            showMessage("error", message);
            throw new Error(message);
        }
    };

    return (
        <>
            <div data-onboarding="client-profile-basic-details">
                <EditableSection
                    title="Basic Details"
                    fields={getBasicDetailsFields(clientDetails)}
                    onSave={saveBasicFields}
                />
            </div>

            {clientDetails.ClientDetails.length > 0 ? (
                clientDetails.ClientDetails.map((detail, index) => (
                    <div
                        key={detail.Client_details_id || index}
                        className="brand-details-section"
                        // Only tag the first one as the onboarding tour's target - there's
                        // just one spotlight step for "Company Details" regardless of how
                        // many company records a client has.
                        data-onboarding={index === 0 ? "client-profile-company-details" : undefined}
                    >
                        <EditableSection
                            title={`Company Details ${index + 1}`}
                            fields={getCompanyDetailsFields(detail)}
                            onSave={(changedFields) => saveCompanyFields(detail.Client_details_id, changedFields)}
                            labelTag="small"
                        />
                    </div>
                ))
            ) : (
                <p>No brand details available.</p>
            )}

            <CustomToast ref={toastRef} />
        </>
    );
}

export default Client_accountdelails;
